<template>
  <div class="row">
    <div class="col-md-12 col-xxl-12 col-sm-12 mb-10">
      <div class="page-wrapper">

        <!-- HEADER -->
        <div class="page-header">
          <div class="header-left">
            <div class="header-icon">
              <i class="flaticon-user"></i>
            </div>
            <div>
              <h4 class="page-title">Informations générales</h4>
              <p class="page-subtitle" v-if="etudiant?.nom">
                {{ etudiant.prenom }} {{ etudiant.nom }}
                <span v-if="etudiant?.numeroMatricule" class="matricule-badge">
                  #{{ etudiant.numeroMatricule }}
                </span>
              </p>
            </div>
          </div>
          <router-link to="/liste-etudiants" class="btn-back">
            <i class="flaticon-left-arrow"></i>
            Retour à la liste
          </router-link>
        </div>

        <!-- GRILLE PRINCIPALE -->
        <div class="info-grid">

          <!-- BLOC : Identité -->
          <div class="info-block"
            v-if="etudiant?.numeroMatricule || etudiant?.nom || etudiant?.prenom || etudiant?.sexe || etudiant?.dateNaissance"
          >
            <div class="block-header">
              <div class="block-icon identity">
                <i class="flaticon-user"></i>
              </div>
              <h6 class="block-title">Identité</h6>
            </div>
            <div class="block-body">
              <div class="field-row" v-if="etudiant?.numeroMatricule">
                <span class="field-label">Matricule</span>
                <span class="field-value mono">{{ etudiant.numeroMatricule }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.nom">
                <span class="field-label">Nom</span>
                <span class="field-value">{{ etudiant.nom }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.prenom">
                <span class="field-label">Prénom</span>
                <span class="field-value">{{ etudiant.prenom }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.sexe">
                <span class="field-label">Sexe</span>
                <span class="field-value">
                  <span class="tag tag-blue">{{ etudiant.sexe }}</span>
                </span>
              </div>
              <div class="field-row" v-if="etudiant?.dateNaissance">
                <span class="field-label">Date de naissance</span>
                <span class="field-value">{{ format_Date(etudiant.dateNaissance) }}</span>
              </div>
            </div>
          </div>

          <!-- BLOC : Contact -->
          <div class="info-block"
            v-if="etudiant?.email || etudiant?.telephone1 || etudiant?.adresse || etudiant?.quartier?.commune?.libelle || etudiant?.quartier?.libelle"
          >
            <div class="block-header">
              <div class="block-icon contact">
                <i class="flaticon-email"></i>
              </div>
              <h6 class="block-title">Contact</h6>
            </div>
            <div class="block-body">
              <div class="field-row" v-if="etudiant?.email">
                <span class="field-label">Email</span>
                <span class="field-value">
                  <a :href="`mailto:${etudiant.email}`" class="link">{{ etudiant.email }}</a>
                </span>
              </div>
              <div class="field-row" v-if="etudiant?.telephone1">
                <span class="field-label">Téléphone</span>
                <span class="field-value">{{ etudiant.telephone1 }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.adresse">
                <span class="field-label">Adresse</span>
                <span class="field-value">{{ etudiant.adresse }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.quartier?.commune?.libelle">
                <span class="field-label">Commune</span>
                <span class="field-value">{{ etudiant.quartier.commune.libelle }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.quartier?.libelle">
                <span class="field-label">Quartier</span>
                <span class="field-value">{{ etudiant.quartier.libelle }}</span>
              </div>
            </div>
          </div>

          <!-- BLOC : Catégories -->
          <div class="info-block"
            v-if="etudiant?.categorieEtudiant || etudiant?.categorieFormateur"
          >
            <div class="block-header">
              <div class="block-icon category">
                <i class="flaticon-layers"></i>
              </div>
              <h6 class="block-title">Catégories</h6>
            </div>
            <div class="block-body">
              <div class="field-row" v-if="etudiant?.categorieEtudiant">
                <span class="field-label">Catégorie étudiant</span>
                <span class="field-value">
                  <span class="tag tag-info">{{ etudiant.categorieEtudiant }}</span>
                </span>
              </div>
              <div class="field-row" v-if="etudiant?.categorieFormateur">
                <span class="field-label">Formateur</span>
                <span class="field-value">
                  <span class="tag tag-success">{{ etudiant.categorieFormateur }}</span>
                </span>
              </div>
            </div>
          </div>

          <!-- BLOC : Informations professionnelles -->
          <div class="info-block"
            v-if="etudiant?.qualification?.libelle || etudiant?.anneeExperience || etudiant?.poste || etudiant?.categorie || etudiant?.biographie"
          >
            <div class="block-header">
              <div class="block-icon pro">
                <i class="flaticon-briefcase"></i>
              </div>
              <h6 class="block-title">Informations professionnelles</h6>
            </div>
            <div class="block-body">
              <div class="field-row" v-if="etudiant?.qualification?.libelle">
                <span class="field-label">Qualification</span>
                <span class="field-value">{{ etudiant.qualification.libelle }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.anneeExperience">
                <span class="field-label">Expérience</span>
                <span class="field-value">
                  <span class="tag tag-orange">{{ etudiant.anneeExperience }} an(s)</span>
                </span>
              </div>
              <div class="field-row" v-if="etudiant?.poste">
                <span class="field-label">Poste occupé</span>
                <span class="field-value">{{ etudiant.poste }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.categorie">
                <span class="field-label">Catégorie prof.</span>
                <span class="field-value">{{ etudiant.categorie }}</span>
              </div>
              <div class="field-row" v-if="etudiant?.biographie">
                <span class="field-label">Infos complémentaires</span>
                <span class="field-value text-muted">{{ etudiant.biographie }}</span>
              </div>
            </div>
          </div>

          <!-- BLOC : Activités -->
          <div class="info-block" v-if="etudiant?.activites?.length">
            <div class="block-header">
              <div class="block-icon activities">
                <i class="flaticon-layers"></i>
              </div>
              <h6 class="block-title">
                Activités
                <span class="count-badge">{{ etudiant.activites.length }}</span>
              </h6>
            </div>
            <div class="block-body">
              <div class="tags-cloud">
                <span
                  v-for="(activite, index) in etudiant.activites"
                  :key="index"
                  class="tag tag-primary"
                >
                  {{ activite.libelle }}
                </span>
              </div>
            </div>
          </div>

          <!-- BLOC : Langues -->
          <div class="info-block" v-if="etudiant?.langues?.length || etudiant?.user?.langues?.length">
            <div class="block-header">
              <div class="block-icon langues">
                <i class="flaticon-globe"></i>
              </div>
              <h6 class="block-title">Langues parlées</h6>
            </div>
            <div class="block-body">
              <div class="tags-cloud">
                <span
                  v-for="(langue, index) in (etudiant.user?.langues || etudiant.langues || [])"
                  :key="index"
                  class="tag tag-warning"
                >
                  {{ langue.libelle || langue }}
                </span>
              </div>
            </div>
          </div>

        </div><!-- /info-grid -->

        <!-- BLOC : Pièces jointes (pleine largeur) -->
        <div class="info-block full-width" v-if="etudiant?.pieces?.length">
          <div class="block-header">
            <div class="block-icon pieces">
              <i class="flaticon-file"></i>
            </div>
            <h6 class="block-title">
              Pièces jointes
              <span class="count-badge">{{ etudiant.pieces.length }}</span>
            </h6>
          </div>
          <div class="block-body p-0">
            <table class="pieces-table">
              <thead>
                <tr>
                  <th>Type de pièce</th>
                   <th>Numéro</th>
                   <th>Aperçu</th>
                   <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(piece, index) in etudiant.pieces" :key="index">
                  <td>
                    <span class="tag tag-secondary">{{ piece.typePiece?.libelle }}</span>
                  </td>
                  <td class="mono">{{ piece.numeroPiece }}</td>
                  <td>
                    <!-- Même pattern que ModalImage dans ListeProduit -->
                    <modal-piece-etudiant
                      v-if="piece"
                      :fileUrl="piece.urlImage"
                      :keyValue="piece.id"
                      :libelle="piece.typePiece?.libelle || 'Pièce jointe'"
                    />
                  </td>
                  <td>
                    <button 
                      @click="confirmDeletePiece(piece)" 
                      class="btn-delete-piece"
                      title="Supprimer cette pièce"
                    >
                      <i class="flaticon-delete"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import ApiService from "@/services/ApiService";
import { error, format_Date, success } from "@/utils/utils";
import { useRoute } from "vue-router";
import ModalPieceEtudiant from "@/components/Etudiant/ModalPieceEtudiant.vue";
import Swal from "sweetalert2";


export default defineComponent({
  name: "ViewEtudiant",
  components: {
    ModalPieceEtudiant,
  },
  setup() {
    const route = useRoute();
    const etudiant = ref<any>(null);

    const getEtudiant = async (id: string) => {
      try {
        const { data } = await ApiService.get(`/etudiants/edit/${id}`);
        etudiant.value = data.data;
        console.log('✅ Données étudiant chargées:', data.data);
        console.log('✅ Pièces reçues:', data.data.pieces);
        if (data.data.pieces && data.data.pieces.length > 0) {
          data.data.pieces.forEach((piece: any, index: number) => {
            console.log(`  Pièce ${index + 1}:`, {
              id: piece.id,
              typePiece: piece.typePiece,
              urlImage: piece.urlImage,
            });
          });
        }
      } catch (err: any) {
        console.error('❌ Erreur chargement étudiant:', err);
        error(err.response?.data?.message || "Erreur lors du chargement de l'étudiant.");
      }
    };

    const confirmDeletePiece = async (piece: any) => {
      const result = await Swal.fire({
        title: 'Confirmer la suppression',
        html: `Voulez-vous vraiment supprimer cette pièce ?<br><strong>${piece.typePiece?.libelle || 'Pièce'}</strong> - ${piece.numeroPiece}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler'
      });

      if (result.isConfirmed) {
        await deletePiece(piece.id);
      }
    };

    const deletePiece = async (pieceId: number) => {
      try {
        await ApiService.delete(`/etudiants/pieces/${pieceId}`);
        success("La pièce a été supprimée avec succès.");
        
        // Recharger les données de l'étudiant
        const id = route.params.id as string;
        if (id) await getEtudiant(id);
      } catch (err: any) {
        console.error('❌ Erreur suppression pièce:', err);
        error(err.response?.data?.message || "Erreur lors de la suppression de la pièce.");
      }
    };

    onMounted(() => {
      const id = route.params.id as string;
      if (id) getEtudiant(id);
    });

    return {
      etudiant,
      format_Date,
      confirmDeletePiece,
    };
  },
});
</script>

<style scoped>
/* ─── Page wrapper ─────────────────────────────── */
.page-wrapper {
  padding: 24px;
  background: #f5f6fa;
  min-height: 100vh;
}

/* ─── Header ───────────────────────────────────── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  flex-shrink: 0;
}
.page-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1d23;
  margin: 0 0 2px;
}
.page-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}
.matricule-badge {
  display: inline-block;
  background: #ede9fe;
  color: #7c3aed;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 20px;
  margin-left: 6px;
  font-family: monospace;
}
.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background .15s;
}
.btn-back:hover { background: #e5e7eb; color: #111827; }

/* ─── Grid ─────────────────────────────────────── */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

/* ─── Block ─────────────────────────────────────── */
.info-block {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  overflow: hidden;
  transition: box-shadow .2s;
}
.info-block:hover { box-shadow: 0 4px 16px rgba(0,0,0,.1); }
.info-block.full-width {
  grid-column: 1 / -1;
  margin-bottom: 0;
}

.block-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid #f0f0f5;
  background: #fafafa;
}
.block-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: #fff;
  flex-shrink: 0;
}
.block-icon.identity  { background: linear-gradient(135deg,#4f46e5,#7c3aed); }
.block-icon.contact   { background: linear-gradient(135deg,#0ea5e9,#0284c7); }
.block-icon.category  { background: linear-gradient(135deg,#f59e0b,#d97706); }
.block-icon.pro       { background: linear-gradient(135deg,#10b981,#059669); }
.block-icon.activities{ background: linear-gradient(135deg,#6366f1,#4f46e5); }
.block-icon.langues   { background: linear-gradient(135deg,#f97316,#ea580c); }
.block-icon.pieces    { background: linear-gradient(135deg,#64748b,#475569); }

.block-title {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: .05em;
  display: flex;
  align-items: center;
  gap: 6px;
}
.count-badge {
  background: #e0e7ff;
  color: #4f46e5;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 20px;
}

.block-body { padding: 6px 0; }
.block-body.p-0 { padding: 0; }

/* ─── Field rows ────────────────────────────────── */
.field-row {
  display: flex;
  align-items: flex-start;
  padding: 9px 18px;
  gap: 12px;
  border-bottom: 1px solid #f5f5f8;
}
.field-row:last-child { border-bottom: none; }
.field-label {
  min-width: 130px;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: .04em;
  padding-top: 2px;
  flex-shrink: 0;
}
.field-value {
  font-size: 14px;
  color: #1f2937;
  flex: 1;
  word-break: break-word;
}
.field-value.mono { font-family: monospace; font-size: 13px; }
.field-value.text-muted { color: #6b7280; font-size: 13px; line-height: 1.5; }
.link { color: #4f46e5; text-decoration: none; }
.link:hover { text-decoration: underline; }

/* ─── Tags ──────────────────────────────────────── */
.tags-cloud {
  padding: 14px 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
}
.tag-primary   { background:#e0e7ff; color:#4338ca; }
.tag-blue      { background:#dbeafe; color:#1d4ed8; }
.tag-info      { background:#cffafe; color:#0e7490; }
.tag-success   { background:#dcfce7; color:#15803d; }
.tag-warning   { background:#fef3c7; color:#b45309; }
.tag-orange    { background:#ffedd5; color:#c2410c; }
.tag-secondary { background:#f1f5f9; color:#475569; }

/* ─── Pieces table ──────────────────────────────── */
.pieces-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.pieces-table thead tr {
  background: #f8fafc;
}
.pieces-table th {
  padding: 12px 18px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: .05em;
  border-bottom: 1px solid #e5e7eb;
}
.pieces-table td {
  padding: 12px 18px;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}
.pieces-table tbody tr:last-child td { border-bottom: none; }
.pieces-table tbody tr:hover { background: #f9fafb; }
.mono { font-family: monospace; font-size: 13px; }

/* ─── Bouton de suppression ─────────────────────── */
.btn-delete-piece {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #fee2e2;
  color: #dc2626;
  font-size: 14px;
  cursor: pointer;
  transition: all .2s;
}
.btn-delete-piece:hover {
  background: #dc2626;
  color: #fff;
  transform: scale(1.05);
}
.btn-delete-piece:active {
  transform: scale(0.95);
}
</style>
